import { useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import PrivateLayout from "@/components/layout/auth/PrivateLayout";
import { useI18n } from "@/hooks/useI18n";
import { useAuth } from "@/hooks/useAuth";
import { useLibraryScope } from "@/lib/library/hooks/useLibraryScope";
import { ACTIVITIES, ActivityType, getActivityModal, getActivityTypeIcon } from "@/lib/builder/models/activity-configs.model";
import { ModalProps } from "@/lib/builder/models/activities-modal.model";
import { ItineraryActivity } from "@/lib/builder/models/activity.model";
import { ActivityTypePicker } from "@/lib/builder/components/ActivityTypePicker";
import { SaveToLibraryModal } from "@/lib/builder/modals/SaveToLibraryModal";
import { LibraryActivitiesTable } from "@/lib/library/LibraryActivitiesTable";
import { LibraryArchiveSelector, LibraryArchiveScope } from "@/lib/library/LibraryArchiveSelector";
import { useActivityLibraryQuery } from "@/lib/library/hooks/useActivityLibrary";
import { LIBRARY_ACTIVITY_STORAGE_ID } from "@/lib/library/models/library-activity.model";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Loader2, Search } from "lucide-react";

// Valore sentinella per l'opzione "tutti i tipi": Radix Select non ammette
// SelectItem con value="" (stringa vuota), quindi usiamo 'all' e lo mappiamo
// su "nessun filtro" (URL senza param `type`).
const ALL_TYPES_VALUE = "all";

/**
 * Pagina "Libreria": una singola vista con un filtro a icone per tipo attività
 * (accommodation, activity, flight, transportation, food-drinks, events, notes),
 * riusando la stessa config `ACTIVITIES` del builder per label/icone così la
 * libreria resta coerente con il linguaggio visivo già noto agli utenti.
 * Il filtro è una select a singola scelta (nessun tipo selezionato = mostra
 * tutti i tipi), coerente col filtro `ownerScope` già esistente in
 * `LibraryActivitiesTable`.
 *
 * La fetch (`useActivityLibraryQuery`) recupera le righe visibili all'utente filtrate
 * PER AMBITO lato server (`p_scope`): tipo attività, tag e ricerca testuale restano poi
 * filtri client-side, per evitare una chiamata di rete per ogni cambio filtro o carattere digitato.
 *
 * Ambiti:
 * - account `agency`: nessuna scelta, scope forzato ad 'agency' (personale ≡ agenzia);
 * - `travel_advisor_member`: sceglie personale/agenzia (`LibraryArchiveSelector`), le due
 *   viste derivano da due query per scope ('user' / 'agency');
 * - utente senza agenzia: scope fisso 'user';
 * - admin: nessuno scope (vede tutto), con il filtro ownerScope già esistente in
 *   `LibraryActivitiesTable`.
 */
const LibraryPage = () => {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userRole, loading: authLoading } = useAuth();
  const { isAgencyAccount, isAgencyMember, canPromote } = useLibraryScope();

  const isAdmin = userRole === 'admin';

  // Il membro agenzia sceglie l'ambito (personale/agenzia) via query param `?scope=`;
  // finché non ha scelto mostriamo il selettore e non facciamo fetch.
  const scopeParam = searchParams.get('scope');
  const memberScope: LibraryArchiveScope | null = isAgencyMember
    ? scopeParam === 'agency' || scopeParam === 'user'
      ? scopeParam
      : null
    : null;
  const needsArchiveChoice = isAgencyMember && !memberScope;

  // Scope effettivo passato alla RPC:
  // - admin: null (tutte le righe visibili);
  // - account agency: 'agency' (personale ≡ agenzia);
  // - membro: lo scope scelto (null finché non sceglie → fetch disabilitata);
  // - utente semplice: 'user'.
  const queryScope: LibraryArchiveScope | null = isAdmin
    ? null
    : isAgencyAccount
      ? 'agency'
      : isAgencyMember
        ? memberScope
        : 'user';

  // La fetch parte SOLO quando l'auth è risolto (`!authLoading`): al primo render
  // `userRole`/`userData` sono null, quindi senza questo gate `queryScope` cadrebbe nel
  // ramo di default ('user') e partirebbe una chiamata spuria, poi rifatta con lo scope
  // reale una volta caricato l'utente (doppione visto per agenzia e per membro). Con l'auth
  // caricato: l'agenzia entra dritta nella libreria agenzia (fetch subito), il membro non
  // fetcha finché non sceglie l'archivio (`needsArchiveChoice`).
  const { data: activities = [], isLoading } = useActivityLibraryQuery(
    { scope: queryScope },
    { enabled: !authLoading && !needsArchiveChoice }
  );

  // Flusso "Aggiungi": scelto un tipo si apre il modal attività corrispondente del
  // builder (senza date, vedi `hideDates`); al salvataggio si incatena lo stesso
  // SaveToLibraryModal già usato dal builder per scegliere tag/ambito e persistere
  // la entry in libreria (stesso pattern di `handleSaveToLibrary` in DayBuilderStep).
  const [newActivityType, setNewActivityType] = useState<ActivityType | null>(null);
  const [activityToSaveToLibrary, setActivityToSaveToLibrary] = useState<ItineraryActivity | null>(null);
  // Ricerca testuale: lo stato vive qui così l'input può stare a fianco della select
  // tipologia; la tabella lo riceve come prop e lo applica al filtro client-side.
  const [search, setSearch] = useState('');

  const closeNewActivityModal = () => setNewActivityType(null);

  const handleNewActivitySave = (activity: Partial<ItineraryActivity>) => {
    setNewActivityType(null);
    setActivityToSaveToLibrary(activity as ItineraryActivity);
  };

  // Il filtro per ambito è ora fatto lato server (`p_scope`), quindi `activities`
  // è già ristretto allo scope corrente: nessun partizionamento client-side qui.
  // Il pulsante "Promuovi in libreria agenzia" ha senso solo per il membro che sta
  // guardando la propria libreria personale.
  const enablePromote = canPromote && memberScope === 'user';

  // "Rimuovi dalla libreria agenzia": ha senso nella vista agenzia, dove il proprietario
  // di una riga può riportarla a sola personale. La tabella la mostra comunque solo sulle
  // righe di cui l'utente corrente è il creatore (owner_id === me), coerente con la RPC.
  const enableDemote = queryScope === 'agency';

  // `accommodationOut` è una variante interna nascosta nel builder (stesso modal
  // di accommodation): non ha senso come filtro separato in libreria.
  const visibleActivityConfigs = useMemo(
    () => ACTIVITIES.filter((activityConfig) => !activityConfig.hidden),
    []
  );

  const typeParam = searchParams.get('type');
  const selectedActivityType: ActivityType | null = useMemo(() => {
    if (!typeParam) return null;
    const validIds = new Set(visibleActivityConfigs.map((activityConfig) => activityConfig.id));
    return validIds.has(typeParam as ActivityType) ? (typeParam as ActivityType) : null;
  }, [typeParam, visibleActivityConfigs]);

  const handleSelectActivityType = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === ALL_TYPES_VALUE) {
      next.delete('type');
    } else {
      next.set('type', value);
    }
    setSearchParams(next);
  };

  const handleScopeSelect = (nextScope: LibraryArchiveScope) => {
    const next = new URLSearchParams(searchParams);
    next.set("scope", nextScope);
    setSearchParams(next);
  };

  const handleChangeArchive = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("scope");
    setSearchParams(next);
  };

  // Per il membro agenzia che ha già scelto un archivio, il titolo della pagina è
  // sostituito dal tasto "Indietro" (torna alla scelta archivio) + il nome
  // dell'archivio corrente ("Personale"/"Agenzia"). Negli altri casi resta il
  // titolo statico della libreria.
  const showArchiveTitle = isAgencyMember && !!memberScope;
  const pageTitle: ReactNode = showArchiveTitle ? (
    <span className="flex items-center gap-2">
      <Button
        variant="ghost"
        size="icon"
        type="button"
        onClick={handleChangeArchive}
        aria-label={t('library.changeArchiveAction')}
        className="h-7 w-7 shrink-0 text-gray-500 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
      </Button>
      <span className="flex flex-col">
        <span>
          {memberScope === 'agency'
            ? t('library.archiveChoiceAgencyAction')
            : t('library.archiveChoicePersonalAction')}
        </span>
        <span className="text-sm font-normal text-gray-600">
          {t('library.pageDescription')}
        </span>
      </span>
    </span>
  ) : (
    t("library.title")
  );

  // Il tasto "Aggiungi" vive nell'header (slot `headerAction`, allineato a destra
  // come i CTA delle altre pagine), non più nella barra dei filtri. Presente solo
  // quando è mostrata la tabella (non durante il loading o la scelta dell'archivio).
  const showLibraryTable = !authLoading && !needsArchiveChoice;
  const headerAction: ReactNode = showLibraryTable ? (
    <ActivityTypePicker
      onSelect={setNewActivityType}
      label={t('library.addActivityAction')}
      variant="default"
      size="lg"
      className="h-8 px-3"
    />
  ) : undefined;

  return (
    <PrivateLayout
      pageTitle={pageTitle}
      headerAction={headerAction}
      // Nel caso archivio il sottotitolo è già dentro `pageTitle` (impilato sotto
      // il nome dell'archivio), quindi non passiamo anche la description del layout.
      pageDescription={showArchiveTitle ? "" : t("library.pageDescription")}
      contentClassName="h-full flex flex-col min-h-0 py-2 md:py-6"
    >
      <div className="flex flex-1 flex-col min-h-0 gap-4">
        {authLoading ? (
          // Finché non conosciamo il ruolo non sappiamo se mostrare il selettore d'archivio
          // (membro) o la libreria diretta (agenzia/utente): mostriamo un loader neutro per
          // non far lampeggiare la tabella prima di switchare al selettore.
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : needsArchiveChoice ? (
          <LibraryArchiveSelector onSelect={handleScopeSelect} />
        ) : (
          <>
            <div className="max-w-6xl w-full mx-auto flex flex-1 flex-col min-h-0 gap-4">
              <div className="flex flex-wrap items-center justify-between gap-2 shrink-0">
                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                  <Select
                    value={selectedActivityType ?? ALL_TYPES_VALUE}
                    onValueChange={handleSelectActivityType}
                  >
                    <SelectTrigger className="w-full sm:w-56" aria-label={t('library.typeFilterLabel')}>
                      <SelectValue placeholder={t('library.typeFilterLabel')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_TYPES_VALUE}>
                        {t('library.typeFilterAllTypesHint')}
                      </SelectItem>
                      {visibleActivityConfigs.map((activityConfig) => (
                        <SelectItem key={activityConfig.id} value={activityConfig.id}>
                          <span className="flex items-center gap-2">
                            {getActivityTypeIcon(activityConfig.id)}
                            {/* `labelKey` è tipizzato `string` in ActivityTypeConfig (usato dal builder generico);
                                stesso cast già adottato da DayBuilderStep.tsx/TimelineCard.tsx per lo stesso campo. */}
                            <span>{t(activityConfig.labelKey as any)}</span> {/* eslint-disable-line @typescript-eslint/no-explicit-any */}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={t('library.tableSearchPlaceholder')}
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>

              <div className="flex-1 min-h-0">
                <LibraryActivitiesTable
                  selectedActivityType={selectedActivityType}
                  search={search}
                  activities={activities}
                  isLoading={isLoading}
                  enablePromote={enablePromote}
                  enableDemote={enableDemote}
                  // In libreria personale (scope 'user') il proprietario è sempre l'utente
                  // corrente: colonna ridondante. La mostriamo solo in vista agenzia
                  // (righe di più membri) o admin (nessuno scope, vede tutto).
                  showOwner={isAdmin || queryScope === 'agency'}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {newActivityType &&
        (getActivityModal(newActivityType)({
          type: newActivityType,
          isOpen: true,
          onClose: closeNewActivityModal,
          onSave: handleNewActivitySave,
          icon: getActivityTypeIcon(newActivityType),
          itineraryId: LIBRARY_ACTIVITY_STORAGE_ID,
          hideDates: true,
        } as ModalProps) as ReactNode)}

      <SaveToLibraryModal
        isOpen={activityToSaveToLibrary !== null}
        onClose={() => setActivityToSaveToLibrary(null)}
        activity={activityToSaveToLibrary}
      />
    </PrivateLayout>
  );
};

export default LibraryPage;
